import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

/**
 * Stacked-modal scrim rule: only the bottom-most layer of stacked modal
 * surfaces dims the screen. Every surface that shows a scrim (the drawer-look
 * current-job page, MMDrawer's overlay) registers as a layer while visible;
 * a layer with another layer registered BEFORE it renders its own scrim
 * transparent, so the fade never darkens beyond the bottom layer's — no
 * matter how the surfaces are stacked.
 *
 * The default (provider-less) value reports no layers, so components consume
 * the hook safely in isolation (tests): they just keep their own scrim.
 */
const ScrimContext = createContext({
  layers: [],
  acquire: () => {
    return {
      id: null,
      release: () => {},
    };
  },
});

const withoutLayer = (layerIds, id) => {
  return layerIds.filter((layerId) => {
    return layerId !== id;
  });
};

const ScrimProvider = ({ children }) => {
  // Layer ids in registration (= stacking) order.
  const [layers, setLayers] = useState([]);
  const nextIdRef = useRef(0);

  const acquire = useCallback(() => {
    nextIdRef.current += 1;
    const id = nextIdRef.current;
    setLayers((prev) => {
      return [...prev, id];
    });
    return {
      id,
      release: () => {
        setLayers((prev) => {
          return withoutLayer(prev, id);
        });
      },
    };
  }, []);

  const value = useMemo(() => {
    return { layers, acquire };
  }, [layers, acquire]);

  return (
    <ScrimContext.Provider value={value}>{children}</ScrimContext.Provider>
  );
};

ScrimProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Registers a scrim layer while `active` and reports whether another layer is
 * already dimming the screen BENEATH this one (registered earlier). Reactive:
 * if the bottom layer goes away while this one is open, this layer starts
 * dimming again.
 *
 * @param {boolean} active Whether this component currently shows a scrim.
 * @returns {boolean} True when a layer beneath already dims the screen.
 */
function useScrimLayer(active) {
  const { layers, acquire } = useContext(ScrimContext);
  const [ownId, setOwnId] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    const { id, release } = acquire();
    setOwnId(id);
    return () => {
      release();
      setOwnId(null);
    };
  }, [active, acquire]);

  if (!active) return false;
  // Registered: beneath = any layer ordered before ours. Not yet registered
  // (first open render, before the effect): any existing layer is beneath.
  return ownId != null ? layers.indexOf(ownId) > 0 : layers.length > 0;
}

export { ScrimProvider, useScrimLayer };
